import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Diamond,
  BarChart3,
  Users,
  Gift,
  FilePlus,
  User,
  Settings as SettingsIcon,
  Shield,
  LayoutGrid,
  Languages,
  LogOut,
  X,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/lib/auth/useAuth';
import { authApi } from '@/features/auth/api';
import { useFeatureAccess } from '@onxite/plan-sdk';
import { useT } from '@/lib/i18n/useT';
import { LANGUAGES, usePrefsStore } from '@/lib/prefs/usePrefsStore';

/**
 * Mobile primary navigation — a fixed bottom bar plus a "More" bottom sheet.
 *
 * Replaces the off-canvas hamburger drawer on phones (`md:hidden`). The bar
 * keeps the three top destinations one tap away; everything else lives in the
 * grid-of-tiles sheet, matching the native-app pattern. Desktop still uses the
 * full `Sidebar`.
 */

interface MobileNavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  to: string;
  match: (path: string) => boolean;
}

export function MobileNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { role, logout } = useAuth();
  const isAdmin = role === 'admin' || role === 'master';
  const planSurveyBuilder = useFeatureAccess('surveyBuilder').enabled;
  const t = useT();
  const [moreOpen, setMoreOpen] = useState(false);

  const language = usePrefsStore((s) => s.language);
  const setLanguage = usePrefsStore((s) => s.setLanguage);
  const currentLang = LANGUAGES.find((l) => l.id === language) ?? LANGUAGES[0];
  const nextLang =
    LANGUAGES[(LANGUAGES.findIndex((l) => l.id === language) + 1) % LANGUAGES.length];

  // Close the sheet whenever the route changes — tapping a tile navigates,
  // and the sheet should not linger over the new page.
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  // Escape closes the sheet (mirrors the desktop drawer behaviour).
  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [moreOpen]);

  const primary: MobileNavItem[] = [
    {
      id: 'home',
      label: t('sidebar.home'),
      icon: <Home className="h-5 w-5" />,
      to: '/',
      match: (p) => p === '/',
    },
    {
      id: 'my-xotskies',
      label: t('sidebar.myXotskies'),
      icon: <Diamond className="h-5 w-5" />,
      to: '/stock',
      match: (p) => p.startsWith('/stock'),
    },
    {
      id: 'insights',
      label: t('sidebar.insights'),
      icon: <BarChart3 className="h-5 w-5" />,
      to: '/insights',
      match: (p) => p.startsWith('/insights'),
    },
  ];

  const more: MobileNavItem[] = [
    {
      id: 'contacts',
      label: t('sidebar.contacts'),
      icon: <Users className="h-6 w-6" />,
      to: '/contacts',
      match: (p) => p.startsWith('/contacts'),
    },
    {
      id: 'redeem',
      label: t('sidebar.redeem'),
      icon: <Gift className="h-6 w-6" />,
      to: '/redeem',
      match: (p) => p.startsWith('/redeem'),
    },
    ...(planSurveyBuilder
      ? [
          {
            id: 'survey-builder',
            label: t('sidebar.surveyBuilder'),
            icon: <FilePlus className="h-6 w-6" />,
            to: '/surveys/builder',
            match: (p: string) => p.startsWith('/surveys/builder'),
          },
        ]
      : []),
    {
      id: 'settings',
      label: t('sidebar.settings'),
      icon: <SettingsIcon className="h-6 w-6" />,
      to: '/settings',
      match: (p) =>
        p.startsWith('/settings') ||
        p.startsWith('/notifications') ||
        p.startsWith('/active-log'),
    },
    {
      id: 'profile',
      label: t('sidebar.profile'),
      icon: <User className="h-6 w-6" />,
      to: '/profile',
      match: (p) => p.startsWith('/profile'),
    },
    ...(isAdmin
      ? [
          {
            id: 'admin',
            label: t('sidebar.admin'),
            icon: <Shield className="h-6 w-6" />,
            to: '/admin',
            match: (p: string) => p.startsWith('/admin'),
          },
        ]
      : []),
  ];

  const isActive = (item: MobileNavItem) => item.match(location.pathname);
  // The "More" tab lights up whenever the current route lives behind it.
  const moreActive = more.some(isActive);

  const handleSignOut = async () => {
    setMoreOpen(false);
    // Clear the server-side single-session record while the token is still
    // valid, then drop local state.
    await authApi.logout().catch(() => undefined);
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* ── Fixed bottom bar ─────────────────────────────────────────── */}
      <nav
        className="fixed inset-x-0 bottom-0 z-40 flex h-16 items-stretch border-t border-[var(--color-xk-border)] bg-[var(--color-xk-bg-nav)] pb-[env(safe-area-inset-bottom)] md:hidden"
        aria-label={t('mobileNav.label')}
      >
        {primary.map((item) => (
          <Link
            key={item.id}
            to={item.to}
            className={cn(
              'flex flex-1 flex-col items-center justify-center gap-1 transition-colors',
              isActive(item)
                ? 'text-[var(--color-xk-accent)]'
                : 'text-[var(--color-xk-text-muted)]',
            )}
          >
            {item.icon}
            <span className="text-[10px] font-bold">{item.label}</span>
          </Link>
        ))}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          aria-expanded={moreOpen}
          className={cn(
            'flex flex-1 flex-col items-center justify-center gap-1 transition-colors',
            moreActive || moreOpen
              ? 'text-[var(--color-xk-accent)]'
              : 'text-[var(--color-xk-text-muted)]',
          )}
        >
          <LayoutGrid className="h-5 w-5" />
          <span className="text-[10px] font-bold">{t('mobileNav.more')}</span>
        </button>
      </nav>

      {/* ── Backdrop ─────────────────────────────────────────────────── */}
      {moreOpen && (
        <button
          type="button"
          aria-label={t('mobileNav.close')}
          onClick={() => setMoreOpen(false)}
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
        />
      )}

      {/* ── "More" bottom sheet ──────────────────────────────────────── */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('mobileNav.more')}
        className={cn(
          'fixed inset-x-0 bottom-0 z-50 max-h-[82vh] overflow-y-auto rounded-t-2xl border-t border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] pb-[env(safe-area-inset-bottom)] transition-transform duration-200 md:hidden',
          moreOpen ? 'translate-y-0' : 'translate-y-full',
        )}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3">
          <span className="h-1 w-10 rounded-full bg-[var(--color-xk-border)]" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-2 pt-3">
          <h2
            className="text-base font-extrabold text-[var(--color-xk-lavender)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {t('mobileNav.more')}
          </h2>
          <button
            type="button"
            onClick={() => setMoreOpen(false)}
            aria-label={t('mobileNav.close')}
            className="grid h-8 w-8 place-items-center rounded-md text-[var(--color-xk-text-muted)] transition-colors hover:bg-[var(--color-xk-surface)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tile grid */}
        <div className="grid grid-cols-4 gap-3 px-4 pb-4 pt-1">
          {more.map((item) => (
            <Link
              key={item.id}
              to={item.to}
              className={cn(
                'flex aspect-square flex-col items-center justify-center gap-2 rounded-xl border p-2 text-center transition-colors',
                isActive(item)
                  ? 'border-[var(--color-xk-accent)] bg-[var(--color-accent-10)] text-[var(--color-xk-accent)]'
                  : 'border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] text-[var(--color-xk-lavender)]',
              )}
            >
              {item.icon}
              <span className="text-[11px] font-semibold leading-tight">
                {item.label}
              </span>
            </Link>
          ))}
        </div>

        {/* Footer — language toggle + sign out */}
        <div className="flex items-center gap-2 border-t border-[var(--color-xk-border)] px-4 py-3">
          <button
            type="button"
            onClick={() => setLanguage(nextLang.id)}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] py-2.5 text-xs font-bold text-[var(--color-xk-lavender)] transition-colors hover:bg-[var(--color-xk-bg-nav)]"
          >
            <Languages className="h-4 w-4" />
            {currentLang.flag} · {currentLang.label}
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] py-2.5 text-xs font-bold text-[var(--color-error)] transition-colors hover:bg-[var(--color-xk-bg-nav)]"
          >
            <LogOut className="h-4 w-4" />
            {t('common.signOut')}
          </button>
        </div>
      </div>
    </>
  );
}
